import { memo, useEffect, useRef, useSyncExternalStore, type ReactElement } from "react";
import { logical } from "../../ui/kit/index.ts";
import type { TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import { PAGE_RULES } from "../rules.ts";
import { slotRect } from "./area-slots.ts";
import type { HudArea } from "./slots.ts";
import type { HudProgress } from "./store.ts";
import type { ToastSource } from "./toast-column.tsx";
import type { LevelUpShown } from "./level-up.ts";
import { MAX_CRACKLES } from "../vfx/definition.ts";
import { PAGE_XP_BAR_FEED } from "../vfx/feed.ts";
import { VFX_REGISTRY } from "../vfx/registry.ts";
import { PAGE_MOTION } from "../page-motion.ts";
import { xpView } from "./xp.ts";
import { XpBarFx, type XpBarFxDom } from "./xp-bar-fx.ts";
import { say } from "../../words.ts";

/**
 * The XP bar in its slot as the area stands it (`slotRect`; as wide as the paged bar where the bar pages) (P2.4-T3,
 * ADR-083 decision 6; the design's `XpBar`): the bar, 5 tall across the slot, in the XP colour over the track. At the
 * cap the bar is the design's plain brass strip with its light top row and no edge. While the level-up toast names the
 * player's level the bar is drawn full, the design's level-up state (P2.4-T5). No readout stands over it (the author's
 * ruling of 2026-10-02, P2.4-T8): the level is the player card's, and the level, the XP and the rate's breakdown are
 * the bar's tooltip (`xp-rate.ts`), shown by the tooltips' controller as the ability bar's are, by hover, keyboard
 * focus, a long press or a click. The bar is the tooltip's anchor through a hit box 4 taller than the bar, reaching up
 * into the gap the readout left, so a finger finds it; it draws only when the progress, the toast or the layout change.
 *
 * At a level-up the bar plays the `xpBarSurge` vfx (P2.4-T53, ADR-134): a wave band along it, a pulse over it and sparks
 * crackling off its top edge, in the game's gold, on spans the bar holds over its fill and moves by whole pixels and stepped
 * strengths, driven frame by frame while the vfx runs and not at all otherwise (`xp-bar-fx.ts`); under reduced motion one
 * still pulse. Nothing about it re-renders the bar.
 */

/**
 * How far the bar's hit box reaches above the bar, in logical pixels: into the free space over it, never the ability
 * bar below.
 */
const HIT_RISE = 4;

/**
 * The bar's track edge, each side, in logical pixels.
 */
const EDGE = 1;

/**
 * The bar's inner width in logical pixels, which the vfx is placed on.
 *
 * @param rect The slot's rectangle.
 * @returns The width inside the track's edges.
 */
function innerWidth(rect: { readonly width: number }): number {
  return rect.width - 2 * EDGE;
}

/**
 * Plays the bar's vfx onto its spans: wakes when one is asked for, writes a frame at a time while one runs, and sleeps
 * after.
 *
 * @param fx The bar's vfx layer element, holding the wave, the pulse and the crackles.
 * @param width The bar's inner width, read each frame so a layout change moves the wave's course.
 * @returns A function that stops it.
 */
function playBarFx(fx: HTMLElement, width: () => number): () => void {
  const wave = fx.querySelector<HTMLElement>("[data-fx=wave]") as HTMLElement;
  const pulse = fx.querySelector<HTMLElement>("[data-fx=pulse]") as HTMLElement;
  const crackles = Array.from(fx.querySelectorAll<HTMLElement>("[data-fx=crackle]"));
  const dom: XpBarFxDom = {
    wave: (left, size) => {
      wave.hidden = left === null;
      if (left !== null) {
        wave.style.left = logical(left);
        wave.style.width = logical(size);
      }
    },
    pulse: (step) => {
      pulse.dataset["step"] = String(step);
      pulse.hidden = step === 0;
    },
    crackle: (index, left, top, light, size) => {
      const spark = crackles[index];
      if (spark === undefined) {
        return;
      }
      spark.hidden = left === null;
      if (left !== null) {
        spark.style.left = logical(left);
        spark.style.top = logical(top);
        spark.style.width = logical(size);
        spark.style.height = logical(size);
        spark.dataset["light"] = light ? "1" : "0";
      }
    },
  };
  const controller = new XpBarFx(PAGE_XP_BAR_FEED, VFX_REGISTRY, () => PAGE_MOTION.reduced(), () => performance.now(), dom);
  let frame = 0;
  const step = (): void => {
    frame = controller.frame(width()) ? requestAnimationFrame(step) : 0;
  };
  const wake = (): void => {
    if (frame === 0) {
      frame = requestAnimationFrame(step);
    }
  };
  const unlisten = PAGE_XP_BAR_FEED.listen(wake);
  return () => {
    unlisten();
    cancelAnimationFrame(frame);
    controller.stop();
  };
}

/**
 * A toast source that never shows, for a page without the level-up toast.
 */
const NO_TOAST: ToastSource<LevelUpShown | null> = { subscribe: () => () => undefined, getSnapshot: () => null };

/**
 * The XP bar.
 *
 * @param props The `area` it is laid out on, the player's `progress`, the page's `levelUp` toast
 *   (absent on a page without it), the tooltips' controller (`tips`, null where no tooltip shows), and `onCommit`,
 *   told after each commit.
 * @returns The slot holding the bar.
 */
export const XpBar = memo(function XpBar({ area, progress, levelUp, tips = null, onCommit }: { readonly area: HudArea; readonly progress: HudProgress; readonly levelUp?: ToastSource<LevelUpShown | null> | undefined; readonly tips?: TipController | null; readonly onCommit: (() => void) | undefined }): ReactElement {
  const toast = useSyncExternalStore((levelUp ?? NO_TOAST).subscribe, (levelUp ?? NO_TOAST).getSnapshot, (levelUp ?? NO_TOAST).getSnapshot);
  const handlers = useTipHandlers(tips);
  useEffect(() => {
    onCommit?.();
  });
  const rect = slotRect("xp", area);
  const fxRef = useRef<HTMLSpanElement>(null);
  const widthRef = useRef(innerWidth(rect));
  widthRef.current = innerWidth(rect);
  useEffect(() => (fxRef.current === null ? undefined : playBarFx(fxRef.current, () => widthRef.current)), []);
  const view = xpView(PAGE_RULES.tables.levels, progress, rect.width - 2 * EDGE, toast === null ? null : toast.level);
  return (
    <div className="ot-hud-slot ot-hud-xp" data-hud-slot="xp" {...handlers} style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      <span className="ot-hud-xp__hit" data-tip="xp:0" data-hud="xp-hit" tabIndex={tips === null ? undefined : 0} role="meter" aria-label={say("hud.xpBar.name")} aria-valuemin={0} aria-valuemax={view.span} aria-valuenow={view.into} aria-valuetext={`${view.levelText}, ${view.valueText}`} style={{ top: logical(-HIT_RISE), width: logical(rect.width), height: logical(rect.height + HIT_RISE) }}>
        <span className={`ot-hud-bar ot-hud-bar--xp${view.capped ? " is-capped" : ""}`} data-hud="xp" style={{ top: logical(HIT_RISE), width: logical(rect.width), height: logical(rect.height) }}>
          <span className="ot-hud-bar__fill" data-hud="xp-fill" data-fill={view.fill} style={{ width: logical(view.fill) }} />
          <span className="ot-hud-xp__fx" data-hud="xp-fx" ref={fxRef} aria-hidden="true">
            <span className="ot-hud-xp__clip">
              <span className="ot-hud-xp__wave" data-fx="wave" hidden />
            </span>
            <span className="ot-hud-xp__pulse" data-fx="pulse" data-step="0" hidden />
            {Array.from({ length: MAX_CRACKLES }, (_, index) => (
              <span key={index} className="ot-hud-xp__crackle" data-fx="crackle" hidden />
            ))}
          </span>
        </span>
      </span>
    </div>
  );
});
